import { AnimatePresence, motion } from "framer-motion";
import { Dialog, DialogPassThroughOptions } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { ReactNode, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { BUTTON_SEVERITIES, Button, ButtonSeverity } from "../Button";
export interface ModalProps {
    title: ReactNode;
    children: ReactNode;
    isOpen?: boolean;
    isLoading?: boolean;
    hasCancelButton?: boolean;
    hasOkButton?: boolean;
    okButtonType?: ButtonSeverity;
    cancelButtonType?: ButtonSeverity;
    onClickCancel?: () => void;
    onClickClose?: () => void;
    onClickOk?: () => void;
    onClickCloseAnimationEnd?: () => void;
    onShow?: () => void;
    cancelText?: string | null;
    okText?: ReactNode | null;
    isOkButtonDisabled?: boolean;
    isOkLoading?: boolean;
    isCancelButtonDisabled?: boolean;
    className?: string;
    okButtonClassName?: string;
    dismissableMask?: boolean;
    pt?: DialogPassThroughOptions;
    footer?: ReactNode;
}

const Modal = forwardRef<Dialog, ModalProps>(function Modal(
    props: ModalProps,
    ref
) {
    const {
        title,
        children,
        isOpen,
        hasCancelButton = false,
        hasOkButton = false,
        okButtonType,
        cancelButtonType = BUTTON_SEVERITIES.NEUTRAL,
        onClickCancel,
        onClickClose = () => {},
        onClickCloseAnimationEnd = () => {},
        onShow = () => {},
        onClickOk,
        cancelText,
        okText,
        isOkButtonDisabled = false,
        isOkLoading = false,
        isCancelButtonDisabled = false,
        className,
        okButtonClassName,
        isLoading = false,
        dismissableMask = true,
        pt,
        footer = undefined,
        ...rest
    } = props;

    const { t } = useTranslation("common");

    const footerContent = footer || (
        <div className="flex justify-end gap-1.5">
            {hasCancelButton && (
                <Button
                    disabled={isCancelButtonDisabled}
                    onClick={onClickCancel}
                    text
                    severity={cancelButtonType}
                    //@ts-ignore
                    label={cancelText || t("cancel")}
                />
            )}
            {hasOkButton && (
                <Button
                    loading={isOkLoading}
                    onClick={onClickOk}
                    disabled={isOkButtonDisabled}
                    className={okButtonClassName}
                    severity={okButtonType}
                    //@ts-ignore
                    label={okText || t("done")}
                />
            )}
        </div>
    );

    return (
        <Dialog
            ref={ref}
            header={title}
            visible={isOpen}
            onHide={onClickClose}
            footer={footerContent}
            className={twMerge("md:min-w-115 relative", className)}
            draggable={false}
            onShow={onShow}
            dismissableMask={dismissableMask}
            pt={{
                ...pt,
                closeButton: {
                    className: "z-60",
                },
                transition: (baseTransitionProps) => ({
                    ...baseTransitionProps,
                    onExited: onClickCloseAnimationEnd,
                    addEndListener: (node, done) => {
                        // use the css transitionend event to mark the finish of a transition
                        node.addEventListener("transitionend", done, false);
                    },
                }),
            }}
            {...rest}
        >
            {isLoading && (
                <div className="absolute w-full h-full top-0 left-0 bg-surface-500/60 rounded-[6px] z-50 m-auto flex justify-center items-center">
                    <ProgressSpinner className="[&>svg>circle]:stroke-primary-500! w-16" />
                </div>
            )}

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="modal-children"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="h-full"
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </Dialog>
    );
});

export default Modal;
